import React, { useCallback } from 'react';
import { View } from 'react-native';
import { noop } from 'lodash';
import { useAppSelector } from 'store/hooks';
import { Button, CardView, rem, Spacer } from 'design-system';
import Amount from 'design-system/Amount';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconStatusPaused from 'design-system/icons/status/IconStatusPaused';
import StatusIcon from 'design-system/StatusIcon';
import Text from 'design-system/Text';
import { muteAll, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { formatDateForItem } from 'utils/formatting';
import Modal from 'utils/packages/Modal';
import { editAutosaveUpcomingPayment } from '../actions';
const UpcomingAutosavePaymentCard = ({ onLight, upcomingPayment }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const [, , , fetch] = useFetchOld(muteAll);
    const editingAutosaveUpcomingPayment = useAppSelector((state) => state.unpersistedPotsReducer.editingUpcomingPayment);
    const updateSkipped = useCallback((skip) => () => {
        fetch(editAutosaveUpcomingPayment(upcomingPayment.id, skip));
    }, [upcomingPayment.id]);
    const handleSkip = useCallback(() => {
        if (upcomingPayment.skipped) {
            Modal.showConfirmation({
                title: 'Undo skip?',
                subtitle: 'This action will move forward with the upcoming direct debit collection.',
                confirmButtonTitle: 'Close',
                cancelButtonTitle: 'Undo',
                onConfirm: noop,
                onPressCancel: updateSkipped(false),
            });
        }
        else {
            Modal.showConfirmation({
                title: 'Skip next payment',
                subtitle: 'This will skip the upcoming direct debit collection and will resume automatically afterwards.',
                confirmButtonTitle: 'Close',
                cancelButtonTitle: 'Skip payment',
                onConfirm: noop,
                onPressCancel: updateSkipped(true),
            });
        }
    }, [upcomingPayment.skipped, updateSkipped]);
    return (<CardView noMarginTop withPadding noMarginBottom style={onLight ? styles.onLight : null}>
      <View style={styles.row}>
        <View style={styles.iconContainer}>
          <IconRocket colorKey="brand"/>
        </View>
        <Spacer.W16 />
        <View style={styles.flex}>
          <View style={styles.row}>
            <View style={styles.flex}>
              <Text Text-16>Autosave</Text>
            </View>
            <Amount showSign Numbers-16 value={upcomingPayment.amount} style={upcomingPayment.skipped ? styles.skipped : null} color={upcomingPayment.skipped ? colors.text.tertiary : colors.text.income}/>
          </View>
          <View style={styles.row}>
            {upcomingPayment.skipped ? (<>
                <IconStatusPaused width={16} height={16} colorKey="secondary" color={colors.cards.onDark} bgColor={colors.elements.secondary}/>
                <Spacer.W8 />
                <Text TextThin-14 Secondary>
                  Skipped
                </Text>
              </>) : (<View style={styles.row}>
                <View style={styles.flex}>
                  <View style={styles.row}>
                    <StatusIcon state="in-progress" width={16} height={16}/>
                    <Spacer.W8 />
                    <View style={styles.flex}>
                      <Text TextThin-14 numberOfLines={1} StatusInformational>
                        Upcoming
                      </Text>
                    </View>
                  </View>
                </View>
                <Spacer.W8 />
                <Text TextThin-14 Secondary>
                  {formatDateForItem(new Date(upcomingPayment.initiationEta))}
                </Text>
              </View>)}
          </View>
        </View>
      </View>
      <Spacer.H16 />
      <Spacer.H8 />
      <Button square brandReversed onPress={handleSkip} loading={editingAutosaveUpcomingPayment} title={upcomingPayment.skipped ? 'Undo skip' : 'Skip this payment'}/>
    </CardView>);
};
const styleSet = createStyleSheets((colors) => ({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    flex: {
        flex: 1,
    },
    skipped: {
        textDecorationStyle: 'solid',
        textDecorationLine: 'line-through',
    },
    onLight: {
        backgroundColor: colors.cards.onLight,
    },
    iconContainer: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        alignItems: 'center',
        borderRadius: rem(42),
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.brandLight,
    },
}));
export default UpcomingAutosavePaymentCard;
